import { useRef, useState, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { IFRSQuestionResponseOptionDTO } from "../../../../../api/Quiz/GetIFRSQuestionResponseOptions";
import { axios } from "../../../../../lib/axios";
import { toast } from "react-toastify";

type UpdateData = string[][];

const useSpreadsheet = () => {
    const [isOpen, setIsOpen] = useState(false);
    const spreadsheetRef = useRef<any>(null);
    const [data, setData] = useState<UpdateData>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [range, setRange] = useState<{ cols: number; rows: number }>({
        cols: 6,
        rows: 6,
    });
    const [currentResponseOption, setCurrentResponseOption] =
        useState<IFRSQuestionResponseOptionDTO | null>(null);
    const [mountKey, setMountKey] = useState(0);
    const [urlToFetch, setUrlToFetch] = useState<string | null>(null);

    // Query for fetching Excel data
    const { data: excelData, isLoading: isQueryLoading, isError, error } = useQuery({
        queryKey: ['excel-parse', urlToFetch],
        queryFn: async () => {
            if (!urlToFetch) return null;
            const res = await axios.get(
                `excel/parse-from-url?url=${encodeURIComponent(urlToFetch)}`
            );
            return res.data.data;
        },
        enabled: !!urlToFetch,

    });

    const updateData = useCallback(
        (newData: ((oldData: UpdateData) => UpdateData) | UpdateData) => {
            if (typeof newData === "function") {
                setData((oldData) => newData(oldData));
            } else {
                setData(newData);
            }
        },
        []
    );

    // Process Excel data when query succeeds
    useEffect(() => {
        if (excelData && !isQueryLoading) {
            const sheetData = excelData;
            const cells = sheetData.cells || {};

            let maxRow = 0;
            let maxCol = 0;

            const colToIdx = (colStr: string) => {
                let idx = 0;
                for (let i = 0; i < colStr.length; i++) {
                    idx = idx * 26 + (colStr.charCodeAt(i) - 64);
                }
                return idx - 1;
            };

            // Find dimensions
            Object.keys(cells).forEach((addr) => {
                const match = addr.match(/^([A-Z]+)(\d+)$/);
                if (match) {
                    const c = colToIdx(match[1]);
                    const r = parseInt(match[2], 10) - 1;
                    if (r > maxRow) maxRow = r;
                    if (c > maxCol) maxCol = c;
                }
            });

            const rows = Math.max(maxRow + 1, 25);
            const cols = Math.max(maxCol + 1, 15);

            console.log(`📊 Creating grid: ${rows}x${cols}`);

            // Create grid
            const gridData: string[][] = Array.from({ length: rows }, () =>
                Array(cols).fill("")
            );

            const cellStyles: Record<string, string> = {};

            // Populate cells
            Object.keys(cells).forEach((addr) => {
                const match = addr.match(/^([A-Z]+)(\d+)$/);
                if (match) {
                    const c = colToIdx(match[1]);
                    const r = parseInt(match[2], 10) - 1;
                    const cellData = cells[addr];
                    const value = cellData.value;

                    if (r < rows && c < cols) {
                        if (value != null) {
                            gridData[r][c] = String(value);
                        }

                        const styleProps: string[] = [];

                        // Background color from fill
                        if (cellData.fill?.fgColor?.argb) {
                            const argb = cellData.fill.fgColor.argb;
                            const hexColor = argb.length === 8
                                ? `#${argb.substring(2)}`
                                : argb.length === 6
                                    ? `#${argb}`
                                    : argb;
                            styleProps.push(`background-color: ${hexColor}`);
                        }

                        // Font formatting
                        if (cellData.font) {
                            if (cellData.font.bold) {
                                styleProps.push("font-weight: bold");
                            }
                            if (cellData.font.italic) {
                                styleProps.push("font-style: italic");
                            }
                            if (cellData.font.underline) {
                                styleProps.push("text-decoration: underline");
                            }
                            if (cellData.font.strike) {
                                styleProps.push("text-decoration: line-through");
                            }
                            if (cellData.font.size) {
                                styleProps.push(`font-size: ${cellData.font.size}pt`);
                            }
                            if (cellData.font.color?.argb) {
                                const argb = cellData.font.color.argb;
                                const hexColor = argb.length === 8
                                    ? `#${argb.substring(2)}`
                                    : argb.length === 6
                                        ? `#${argb}`
                                        : argb;
                                styleProps.push(`color: ${hexColor}`);
                            }
                            if (cellData.font.name) {
                                styleProps.push(`font-family: ${cellData.font.name}`);
                            }
                        }

                        // Text alignment
                        if (cellData.alignment) {
                            if (cellData.alignment.horizontal) {
                                styleProps.push(`text-align: ${cellData.alignment.horizontal}`);
                            }
                            if (cellData.alignment.vertical) {
                                const vAlignMap: Record<string, string> = {
                                    top: "top",
                                    middle: "middle",
                                    center: "middle",
                                    bottom: "bottom",
                                };
                                const vAlign =
                                    vAlignMap[cellData.alignment.vertical] || "middle";
                                styleProps.push(`vertical-align: ${vAlign}`);
                            }
                            if (cellData.alignment.wrapText) {
                                styleProps.push("white-space: normal");
                                styleProps.push("word-wrap: break-word");
                            }
                        }

                        // Borders
                        if (cellData.border) {
                            const borderStyle = "1px solid #000";
                            if (cellData.border.top) {
                                styleProps.push(`border-top: ${borderStyle}`);
                            }
                            if (cellData.border.bottom) {
                                styleProps.push(`border-bottom: ${borderStyle}`);
                            }
                            if (cellData.border.left) {
                                styleProps.push(`border-left: ${borderStyle}`);
                            }
                            if (cellData.border.right) {
                                styleProps.push(`border-right: ${borderStyle}`);
                            }
                        }

                        if (styleProps.length > 0) {
                            cellStyles[addr] = styleProps.join("; ");
                        }
                    }
                }
            });

            const columns = Array.from({ length: cols }, () => ({
                type: "text",
                width: 100,
                wordWrap: true,
            }));

            const newResult = {
                worksheets: [
                    {
                        name: sheetData.sheetName || "Sheet1",
                        data: gridData,
                        columns: columns,
                        styles: cellStyles,
                    },
                ],
            };

            console.log("✅ Loaded spreadsheet with formatting:", {
                optionId: currentResponseOption?.id,
                rows: gridData.length,
                cols: columns.length,
                styledCells: Object.keys(cellStyles).length,
                mountKey,
            });

            setResult(newResult);
            setData(gridData);
            setIsLoading(false);
        }
    }, [excelData, isQueryLoading, currentResponseOption?.id, mountKey]);

    // Handle query error
    useEffect(() => {
        if (isError && urlToFetch) {
            console.error("❌ Error loading template:", error);
            toast.error(
                "Failed to load spreadsheet template. Using blank spreadsheet."
            );
            setIsLoading(false);
        }
    }, [isError, error, urlToFetch]);

    const handleOpenSpreadsheet = useCallback(
        async (option: IFRSQuestionResponseOptionDTO) => {
            console.log("🔄 Opening spreadsheet for option:", option.id);

            // STEP 1: Increment mount key FIRST to force unmount
            setMountKey(prev => {
                console.log("📈 Incrementing mountKey:", prev, "->", prev + 1);
                return prev + 1;
            });

            // STEP 2: Clear ref
            if (spreadsheetRef.current) {
                console.log("🗑️ Clearing spreadsheet ref");
                spreadsheetRef.current = null;
            }

            // STEP 3: Reset all state and clear previous URL to cancel any in-flight requests
            console.log("🧹 Resetting state");
            setUrlToFetch(null); // Clear URL first to cancel previous query
            setData([]);
            setResult(null);
            setCurrentResponseOption(option);
            setIsLoading(true);
            setIsOpen(true);

            // STEP 4: Wait a tick to ensure unmount happens and state is cleared
            await new Promise(resolve => setTimeout(resolve, 0));

            // Load template if available - DON'T set initial data until we know if there's a template
            if (option.answerFileUrl && option.answerFileUrl !== "none") {
                console.log("📥 Triggering query for template from:", option.answerFileUrl);
                setUrlToFetch(option.answerFileUrl);
                // Query will handle setting data when it completes
            } else {
                console.log("ℹ️ No template URL, using empty spreadsheet");
                // Only now set empty data if there's no template to load
                const initialData = Array.from({ length: 25 }, () => Array(15).fill(""));
                const initialColumns = Array.from({ length: 15 }, () => ({
                    type: "text",
                    width: 100,
                }));

                console.log("📝 Setting initial empty data");
                setResult({
                    worksheets: [
                        {
                            name: "Sheet1",
                            data: initialData,
                            columns: initialColumns,
                        },
                    ],
                });
                setData(initialData);
                setIsLoading(false);
            }
        },
        []
    );

    const reset = useCallback(() => {
        console.log("🔄 Resetting spreadsheet hook");
        setData([]);
        setResult(null);
        setCurrentResponseOption(null);
        setIsLoading(false);
        setUrlToFetch(null);
        if (spreadsheetRef.current) {
            spreadsheetRef.current = null;
        }
    }, []);

    const setIsOpenWithCleanup = useCallback(
        (open: boolean | ((prev: boolean) => boolean)) => {
            if (typeof open === "function") {
                setIsOpen(open);
            } else {
                setIsOpen(open);
                if (!open) {
                    setTimeout(reset, 300);
                }
            }
        },
        [reset]
    );

    return {
        isOpen,
        setIsOpen: setIsOpenWithCleanup,
        spreadsheetRef,
        data,
        setData: updateData,
        range,
        setRange,
        currentResponseOption,
        setCurrentResponseOption,
        isLoading,
        setIsLoading,
        result,
        setResult,
        reset,
        handleOpenSpreadsheet,
        mountKey,
    };
};

export default useSpreadsheet;